import { addDays, subDays, min as earliestOf, max as latestOf } from "date-fns";
import { COMPILATION_MAX_RANGE_DAYS, COMPILATION_MAX_RANGE_MS } from "../../../client-messages/compilation";

/**
 * Combines a date and a validated `"HH:MM"` string into a single `Date`.
 *
 * Returns the date untouched when the time is not yet a complete `"HH:MM"` —
 * the bounds below then fall back to whole-day arithmetic, which is harmless
 * because the form blocks submission on an invalid time anyway.
 */
export function combineDateAndTime(date: Date, time: string): Date {
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match) return date;
  const combined = new Date(date);
  combined.setHours(Number(match[1]), Number(match[2]), 0, 0);
  return combined;
}

/**
 * The latest end date that can still produce a legal range, given the times
 * currently entered.
 *
 * A fixed `startDate + COMPILATION_MAX_RANGE_DAYS` ceiling is only correct when
 * the end time is no later in the day than the start time. With the form's
 * defaults (00:00 to 23:59) it is a day too generous, so the picker offered the
 * advertised maximum span and the form then rejected it — the user had to
 * shorten a selection the picker had just presented as valid.
 *
 * Deriving the bound from the actual times fixes that in both directions: it
 * yields `start + 3` while the end time is 23:59, and widens to `start + 4` as
 * soon as the end time is early enough for a full-length range to fit.
 */
export function latestEndDate({
  startDate,
  startTime,
  endTime,
  maxDate,
}: {
  readonly startDate: Date;
  readonly startTime: string;
  readonly endTime: string;
  readonly maxDate?: Date | undefined;
}): Date {
  const startInstant = combineDateAndTime(startDate, startTime).getTime();

  let candidate = addDays(startDate, COMPILATION_MAX_RANGE_DAYS);
  while (
    candidate > startDate &&
    combineDateAndTime(candidate, endTime).getTime() - startInstant > COMPILATION_MAX_RANGE_MS
  ) {
    candidate = subDays(candidate, 1);
  }

  return maxDate ? earliestOf([candidate, maxDate]) : candidate;
}

/**
 * The earliest start date that can still produce a legal range, given the times
 * currently entered. The mirror of {@link latestEndDate}, and time-blind for
 * the same reason before this.
 */
export function earliestStartDate({
  endDate,
  startTime,
  endTime,
  minDate,
}: {
  readonly endDate: Date;
  readonly startTime: string;
  readonly endTime: string;
  readonly minDate?: Date | undefined;
}): Date {
  const endInstant = combineDateAndTime(endDate, endTime).getTime();

  let candidate = subDays(endDate, COMPILATION_MAX_RANGE_DAYS);
  while (
    candidate < endDate &&
    endInstant - combineDateAndTime(candidate, startTime).getTime() > COMPILATION_MAX_RANGE_MS
  ) {
    candidate = addDays(candidate, 1);
  }

  return minDate ? latestOf([candidate, minDate]) : candidate;
}
